import { aiCompleteJson } from './aiService';
import { CALL_OUTCOMES } from '../constants/callOutcomes';

/** IDs für KI-Features – zentral registriert für Settings & Logging */
export const AI_FEATURE_IDS = {
  CALL_OUTCOME: 'call_outcome',
  CALL_NOTES: 'call_notes',
  EMAIL_DRAFT: 'email_draft',
  LEAD_SUMMARY: 'lead_summary',
  OBJECTION_REPLY: 'objection_reply',
};

export const AI_FEATURE_LABELS = {
  [AI_FEATURE_IDS.CALL_OUTCOME]: 'Anrufergebnis aus Notizen',
  [AI_FEATURE_IDS.CALL_NOTES]: 'Gesprächsnotizen strukturieren',
  [AI_FEATURE_IDS.EMAIL_DRAFT]: 'E-Mail-Entwurf',
  [AI_FEATURE_IDS.LEAD_SUMMARY]: 'Lead-Zusammenfassung',
  [AI_FEATURE_IDS.OBJECTION_REPLY]: 'Einwand-Antwort (personalisiert)',
};

const OUTCOME_CATALOG = CALL_OUTCOMES.map(o => `- ${o.id}: ${o.label}`).join('\n');

export async function inferCallOutcomeWithAi(notes, { leadName, company } = {}) {
  const prompt = `Du bist ein erfahrener Vertriebs-CRM-Assistent. Lies die Gesprächsnotizen eines ausgehenden Anrufs und wähle genau EIN passendes Anrufergebnis.

Kontext:
- Lead: ${leadName || 'unbekannt'}${company ? `\n- Firma: ${company}` : ''}
- Notizen (Stichworte des Mitarbeiters, Deutsch): """
${String(notes).trim()}
"""

Erlaubte Ergebnisse (slug → Bedeutung):
${OUTCOME_CATALOG}

Wichtige Unterscheidungen:
- gatekeeper: Nur Assistenz/Sekretariat/Empfang erreicht, GF in Meeting, „später melden/rückrufen um 15 Uhr“, „noch im Termin bis …“, kein Gespräch mit Entscheider
- reached_appt: Termin/Demo/Meeting mit Entscheider oder relevantem Gesprächspartner konkret vereinbart
- decision_maker: Entscheider/GF persönlich erreicht und gesprochen, aber kein Termin fixiert
- callback: Rückruf zu bestimmter Zeit vereinbart (mit Entscheider oder klar als Rückruf-Termin)
- reached_followup: Erreicht, Interesse oder Unterlagen/Follow-up, aber unklar oder offen
- reached_no: Erreicht, aber kein Interesse / Absage
- not_voicemail, not_busy, not_noanswer: Nicht erreicht (Mailbox, besetzt, kein Anschluss)

JSON-Format:
{ "outcome": "<slug oder null>", "confidence": "high"|"medium"|"low", "reason": "<max 12 Wörter auf Deutsch>" }

Bei echter Mehrdeutigkeit: outcome null, confidence low.`;

  const parsed = await aiCompleteJson({ prompt, maxTokens: 120, tier: 'fast' });
  const slug = parsed.outcome && parsed.outcome !== 'null' ? String(parsed.outcome).trim() : null;
  const confidence = ['high', 'medium', 'low'].includes(parsed.confidence) ? parsed.confidence : 'medium';
  return {
    slug,
    confidence: slug ? confidence : 'none',
    reason: parsed.reason || '',
  };
}

export async function structureCallNotesWithAi({ notes, leadName, company, outcome }) {
  const prompt = `Strukturiere diese Gesprächsnotizen in einen professionellen Gesprächsbericht.

Lead: ${leadName || 'Unbekannt'}${company ? ` · ${company}` : ''}
Ergebnis: ${outcome || 'Unbekannt'}
Rohnotizen: ${notes}

JSON: { "note": "strukturierter Bericht in 2-4 Sätzen", "nextStep": "konkreter nächster Schritt auf Deutsch" }`;

  return aiCompleteJson({ prompt, maxTokens: 300, tier: 'fast' });
}

/** E-Mail-Entwurf aus Kontext (für künftige FloatingEmail-Integration) */
export async function draftEmailWithAi({ leadName, company, email, branch, context, tone = 'professionell' }) {
  const prompt = `Schreibe eine kurze Vertriebs-E-Mail auf Deutsch (Ton: ${tone}) für Adstark Recruiting.

Lead: ${leadName || 'Unbekannt'}${company ? ` · ${company}` : ''}${email ? ` · ${email}` : ''}${branch ? ` · Branche: ${branch}` : ''}
Kontext / Stichworte des Nutzers: ${context || 'Follow-up nach Erstkontakt'}

Regeln: professionell, konkret, max. 120 Wörter im Body, Sie-Anrede wenn unklar.
JSON: { "subject": "Betreffzeile", "body": "E-Mail mit Anrede und Gruß (Zeilenumbrüche mit \\n)" }`;

  return aiCompleteJson({ prompt, maxTokens: 500, tier: 'fast' });
}

/** Kurze Lead-Zusammenfassung aus Aktivitäten/Notizen */
export async function summarizeLeadWithAi({ leadName, company, status, snippets }) {
  const prompt = `Fasse diesen CRM-Lead in 3–4 Sätzen auf Deutsch zusammen (für Vertrieb).

Lead: ${leadName}${company ? ` · ${company}` : ''}
Status: ${status || 'unbekannt'}
Aktivitäten/Notizen:
${(snippets || []).slice(0, 12).map((s, i) => `${i + 1}. ${s}`).join('\n') || '(keine)'}

JSON: { "summary": "…", "suggestedNextStep": "…" }`;

  return aiCompleteJson({ prompt, maxTokens: 280, tier: 'fast' });
}

/** Personalisierte Einwand-Antwort für aktuellen Lead */
export async function objectionReplyWithAi({
  objectionTitle,
  templateResponse,
  leadName,
  company,
  branch,
  callContext,
}) {
  const prompt = `Du bist Vertriebsprofi (Recruiting B2B, Deutsch). Passe die Einwand-Antwort an den Lead an.

Einwand: ${objectionTitle}
Standard-Antwort (Vorlage): """${templateResponse}"""
Lead: ${leadName || 'unbekannt'}${company ? ` · ${company}` : ''}${branch ? ` · Branche: ${branch}` : ''}
${callContext ? `Aktueller Gesprächskontext: ${callContext}` : ''}

Schreibe eine gesprochene Antwort (2–4 Sätze), natürlich, nicht abgelesen. Behalte die Strategie der Vorlage.
JSON: { "response": "…" }`;

  const parsed = await aiCompleteJson({ prompt, maxTokens: 220, tier: 'fast' });
  return { response: String(parsed.response || '').trim() };
}
